import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useItemDialogFocus from "../useItemDialogFocus";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import { isMainInventoryLocation, isUnavailableMainLocation } from "../../utils/receivingLocationPolicy";
import "./ItemClassificationPicker.css";

export default function DeliveryNoteLocationPicker({ value, locations, label, onChange, onCancel, disabled, compact = false, generalLocationId, automatic = false, resolvedName, requestOpen = 0, selectionRequired = false, requestMessage, renderContent, onOpenChange }) {
  const { t, themeId } = useReceivingPopupUi();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const previousRequest = useRef(requestOpen);
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);
  useEffect(() => {
    const requested = requestOpen > 0 && requestOpen !== previousRequest.current;
    previousRequest.current = requestOpen;
    if (requested) { setQuery(""); setOpen(true); }
  }, [requestOpen]);
  const close = () => { setOpen(false); onCancel?.(); };
  const dialogRef = useItemDialogFocus(open, false, close);
  const selected = locations.find((location) => String(location.id) === String(value));
  const isMain = isMainInventoryLocation;
  const hasOtherLocations = locations.some((location) => !isMain(location) && location.is_active !== false);
  const available = locations.filter((location) => location.is_active !== false && location.can_receive !== false
    && location.is_receiving_destination !== false && !(hasOtherLocations && isMain(location)));
  const general = available.find((location) => String(location.id) === String(generalLocationId));
  const needsSelection = selectionRequired || (!disabled && isUnavailableMainLocation(value, locations));
  const locationName = needsSelection ? t("Select a receiving location")
    : resolvedName || selected?.name || (value ? t("Location name unavailable") : t(automatic ? "Select a receiving location" : "Automatic by item"));
  const displayName = automatic && value && !needsSelection ? `${locationName} · ${t("Automatic")}` : locationName;
  const filtered = available.filter((location) => `${location.name || ""} ${location.code || ""}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const choose = (id, source) => { if (onChange(id, source) !== false) setOpen(false); };
  const selectionMessage = requestMessage ?? "Select a destination for each unresolved item before verifying the delivery note.";

  const renderLocations = () => <>

            {requestOpen > 0 && selectionMessage && <p role="status">{t(selectionMessage)}</p>}
            <input type="search" value={query} aria-label={t("Search locations")} placeholder={t("Search locations")}
              onChange={(event) => setQuery(event.target.value)} />
            <div className="icp-cards">
              <button type="button" className="icp-card" aria-pressed={!value} onClick={() => choose(compact ? Number(general?.id) || null : null, "header")}>
                {compact ? `${t("Use general location")}: ${general?.name || general?.code || t("Automatic by item")}` : t("Automatic by item")}
              </button>
              {filtered.map((location) => <button type="button" className="icp-card" key={location.id}
                aria-pressed={String(location.id) === String(value)} onClick={() => choose(Number(location.id), "manual")}>
                <strong>{location.name || location.code}</strong>{location.code && <small>{location.code}</small>}
              </button>)}
            </div>
            {!filtered.length && <p role="status">{t("No matching locations.")}</p>}

  </>;

  return <>
    {disabled ? <span className={compact ? "edn-lineLocation" : "edn-value"}>{compact && `${t("Location")}: `}{displayName}</span> :
      <button type="button" className={compact ? "edn-lineLocation edn-lineLocation--editable" : "edn-locationTrigger form-control"}
        aria-label={label} aria-haspopup="dialog" aria-expanded={open} data-invalid={needsSelection || undefined} title={label}
        onClick={() => { setQuery(""); setOpen(true); }}>
        {compact && `${t("Location")}: `}{displayName}<span aria-hidden="true"> ▾</span>
      </button>}
    {open && !disabled && createPortal(
      <div className="pilcore-purchasing-ui icp-overlay" data-theme={themeId} onClick={(event) => { event.stopPropagation(); if (event.currentTarget === event.target) close(); }}>
        <section ref={dialogRef} className="icp-dialog" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
          <header><h3>{label}</h3><button type="button" className="icp-button" onClick={close}>{t("Close")}</button></header>
          <div className="icp-content">{renderContent ? renderContent({ close, content: renderLocations() }) : renderLocations()}</div>
        </section>
      </div>, document.body
    )}
  </>;
}
